/// <reference types="google.maps" />
import { h, render } from 'vue'
import MapMarker from '~/components/custom-ui/MapMarker.vue'

export interface MapLocation {
  lat: number
  lng: number
}

export interface MapOptions {
  center: MapLocation
  zoom: number
}

export interface PlaceOption {
  value: string
  label: string
  description?: string
}

export function useGoogleMap() {
  const googleMapsRef = ref<any>()
  const userStore = useUserStore()

  const mapOptions = ref<MapOptions>({
    center: {
      lat: 25.081233,
      lng: 55.228174,
    },
    zoom: 6,
  })

  const selectedLocation = ref<MapLocation | null>(null)
  const selectedAddress = ref<string | null>(null)
  const selectedPlaceId = ref<string | null>(null)
  const currentUserLocation = ref<MapLocation | null>(null)
  const isLoadingLocation = ref(false)
  const autocompleteOptions = ref<any[]>([])

  async function getCurrentLocation(): Promise<MapLocation> {
    return new Promise<MapLocation>((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser'))
        return
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          })
        },
        (error) => {
          reject(error)
        },
        {
          enableHighAccuracy: false, // Используем менее точный, но более быстрый метод
          timeout: 15000, // Увеличиваем таймаут до 15 секунд
          maximumAge: 60000, // Разрешаем использовать кэшированные координаты до 1 минуты
        }
      )
    })
  }

  /**
   * Проверить доступность Google Maps API
   */
  function isGoogleMapsAvailable(): boolean {
    return typeof window !== 'undefined' && 
           typeof google !== 'undefined' && 
           google?.maps && 
           typeof google.maps.Geocoder === 'function'
  }

  /**
   * Получить адрес из координат через Google Geocoding API
   */
  async function getAddressFromCoordinates(lat: number, lng: number): Promise<string | null> {
    // Проверяем доступность Google Maps API перед использованием
    if (!isGoogleMapsAvailable()) {
      console.warn('Google Maps API is not available. Geocoding skipped.')
      return null
    }

    try {
      const geocoder = new google.maps.Geocoder()
      const response = await geocoder.geocode({
        location: { lat, lng },
      })

      if (response.results && response.results.length > 0) {
        const accurateResult = response.results.find(
          (result) => result.geometry.location_type === 'APPROXIMATE' // ROOFTOP | RANGE_INTERPOLATED | GEOMETRIC_CENTER | APPROXIMATE
        )

        if (accurateResult) {
          return accurateResult.formatted_address
        }

        return response.results[0].formatted_address
      }
      return null
    } catch (error) {
      console.error('Geocoding error:', error)
      return null
    }
  }

  const markerContent = computed(() => {
    // Создаем контейнер для рендеринга Vue компонента
    const container = document.createElement('div')
    // Важно: контейнер должен быть позиционирован относительно
    container.style.position = 'relative'
    container.style.width = '0'
    container.style.height = '0'
    
    // Рендерим компонент маркера в контейнер
    const vnode = h(MapMarker)
    render(vnode, container)
    
    return container
  })

  async function handleMapClick(
    event: any,
    onLocationSelected: (location: MapLocation, address: string | null) => void
  ) {
    const map = event?.map || googleMapsRef.value?.map

    if (!map) {
      console.error('Map not ready')
      return
    }

    // Проверяем доступность Google Maps API
    if (!isGoogleMapsAvailable()) {
      console.warn('Google Maps API is not available. Map click handler skipped.')
      return
    }

    await nextTick()

    try {
      const clickHandler = async (clickEvent: google.maps.MapMouseEvent) => {
        if (clickEvent.latLng) {
          const lat = clickEvent.latLng.lat()
          const lng = clickEvent.latLng.lng()

          selectedLocation.value = { lat, lng }
          selectedPlaceId.value = null

          const address = await getAddressFromCoordinates(lat, lng)
          selectedAddress.value = address

          onLocationSelected({ lat, lng }, address)
        }
      }

      if (typeof map.addListener === 'function') {
        map.addListener('click', clickHandler)
      } else {
        google.maps.event.addListener(map, 'click', clickHandler)
      }
    } catch (error) {
      console.error('Error adding map listener:', error)
    }
  }


  async function loadPlaceOptions(query?: string): Promise<PlaceOption[]> {
    if (!query || query.length < 2) {
      return []
    }

    // Проверяем доступность Google Maps API перед использованием
    if (!isGoogleMapsAvailable() || !google.maps.places?.AutocompleteService) {
      console.warn('Google Maps Places API is not available. Autocomplete skipped.')
      return []
    }

    try {
      const currentCity = userStore.currentCity

      const autocompleteService = new google.maps.places.AutocompleteService()

      // Настройки для ограничения поиска городом
      const request: google.maps.places.AutocompletionRequest = {
        input: query,
        language: 'en',
      }

      // Если есть текущий город, ограничиваем поиск
      if (currentCity?.name) {
        request.componentRestrictions = {
          country: 'AE',
        }
        // Добавляем название города в запрос для лучших результатов
        request.input = `${query}, ${currentCity.name}`
      }

      return new Promise<PlaceOption[]>((resolve, reject) => {
        autocompleteService.getPlacePredictions(request, (predictions, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
            autocompleteOptions.value = predictions
            const results = predictions.map((prediction) => ({
              value: prediction.place_id,
              label: prediction.structured_formatting.main_text,
              description: prediction.structured_formatting.secondary_text,
            }))
            resolve(results)
          } else if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
            resolve([])
          } else {
            console.error('Places API error:', status)
            resolve([])
          }
        })
      })
    } catch (error) {
      console.error('Error loading places:', error)
      return []
    }
  }

  /**
   * Получить детали места по place_id
   */
  async function getPlaceDetails(placeId: string): Promise<google.maps.places.PlaceResult | null> {
    return new Promise<google.maps.places.PlaceResult | null>((resolve, reject) => {
      if (!googleMapsRef.value?.map) {
        reject(new Error('Map not ready'))
        return
      }

      // Проверяем доступность Google Maps Places API
      if (!isGoogleMapsAvailable() || !google.maps.places?.PlacesService) {
        console.warn('Google Maps Places API is not available. Place details skipped.')
        resolve(null)
        return
      }

      const placesService = new google.maps.places.PlacesService(googleMapsRef.value.map)

      placesService.getDetails(
        {
          placeId: placeId,
          fields: ['geometry', 'formatted_address', 'address_components', 'name'],
        },
        (place, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && place) {
            resolve(place)
          } else {
            console.error('Place details error:', status)
            resolve(null)
          }
        }
      )
    })
  }

  /**
   * Обработать выбор места из результатов поиска
   */
  async function handlePlaceSelection(
    placeId: string,
    onLocationSelected: (location: MapLocation, address: string | null) => void
  ) {
    try {
      const place = await getPlaceDetails(placeId)
      if (place?.geometry?.location) {
        const lat = place.geometry.location.lat()
        const lng = place.geometry.location.lng()

        selectedLocation.value = { lat, lng }
        selectedAddress.value = place.formatted_address || place.name || null

        onLocationSelected({ lat, lng }, selectedAddress.value)
      }
    } catch (error) {
      console.error('Error getting place details:', error)
    }
  }

  /**
   * Очистить выбор местоположения
   */
  function clearSelection() {
    selectedLocation.value = null
    selectedAddress.value = null
    selectedPlaceId.value = null
    mapOptions.value.zoom = 6
  }

  /**
   * Перейти к текущему местоположению пользователя
   */
  async function goToCurrentLocation(
    onLocationSelected: (location: MapLocation, address: string | null) => void
  ) {
    isLoadingLocation.value = true
    try {
      const location = await getCurrentLocation()
      currentUserLocation.value = location
      selectedLocation.value = location
      selectedPlaceId.value = null

      // Получаем адрес
      const address = await getAddressFromCoordinates(location.lat, location.lng)
      selectedAddress.value = address

      // Вызываем колбэк
      onLocationSelected(location, address)
    } catch (error: any) {
      // Логируем только если это не таймаут (таймаут - это нормальная ситуация)
      if (error?.code !== 3) {
        console.error('Error getting location:', error?.message || error)
      }
      // Не вызываем колбэк при ошибке, чтобы пользователь мог попробовать снова
    } finally {
      isLoadingLocation.value = false
    }
  }

  /**
   * Инициализация карты при монтировании
   */
  async function initializeMap() {
    try {
      const location = await getCurrentLocation()
      currentUserLocation.value = location
      mapOptions.value.center = location
    } catch (error: any) {
      // При ошибке геолокации используем дефолтные координаты
      const defaultLocation: MapLocation = {
        lat: 25.081233,
        lng: 55.228174,
      }
      currentUserLocation.value = null
      mapOptions.value.center = defaultLocation
      
      // Логируем только если это не таймаут (таймаут - это нормальная ситуация)
      if (error?.code !== 3) {
        console.warn('Error getting location, using default coordinates:', error?.message || error)
      }
    }
  }

  /**
   * Обновить центр карты и приближение при выборе локации
   */
  watch(selectedLocation, async (newVal) => {
    if (newVal) {
      mapOptions.value.center = newVal
      if (!selectedPlaceId.value && isGoogleMapsAvailable()) {
        selectedAddress.value = await getAddressFromCoordinates(newVal.lat, newVal.lng)
      }
    }
  })

  

  return {
    // Refs
    googleMapsRef,
    mapOptions,
    selectedLocation,
    selectedAddress,
    selectedPlaceId,
    currentUserLocation,
    isLoadingLocation,
    markerContent,
    autocompleteOptions,

    // Methods
    getCurrentLocation,
    getAddressFromCoordinates,
    handleMapClick,
    loadPlaceOptions,
    getPlaceDetails,
    handlePlaceSelection,
    clearSelection,
    goToCurrentLocation,
    initializeMap,
  }
}

